import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material'
import * as yup from 'yup'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { EditIcon } from 'src/v3/core/icons'
import CheckIcon from '@mui/icons-material/Check'
import { ClearIcon } from '@mui/x-date-pickers'
import { useFormik } from 'formik'
import { useState } from 'react'
import {
  errorMessageChanged,
  fetchLinkedServices,
  selectActiveLinkedService,
  selectLinkedServiceById,
} from '../manage.slice'

export default () => {
  let dispatch = useAppDispatch()
  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let linkedService = useAppSelector(
    selectLinkedServiceById(activeLinkedService),
  )
  let [
    updatingAssignmentCompletionThreshold,
    setUpdatingAssignmentCompletionThreshold,
  ] = useState<boolean>(false)
  let [
    editingAssignmentCompletionThreshold,
    setEditingAssignmentCompletionThreshold,
  ] = useState<boolean>(false)

  let form = useFormik({
    validationSchema: yup.object({
      assignmentCompletionThreshold: yup.number().integer().min(0).required(),
    }),
    enableReinitialize: true,
    initialValues: {
      assignmentCompletionThreshold: linkedService?.completionThreshold || 0,
    },
    onSubmit: (values) => {
      setUpdatingAssignmentCompletionThreshold(true)
      axios
        .put('/api/manage/linked-service/threshold', {
          id: activeLinkedService,
          completionThreshold: values.assignmentCompletionThreshold,
        })
        .then(() => dispatch(fetchLinkedServices()))
        .then(() => setUpdatingAssignmentCompletionThreshold(false))
        .then(() => form.resetForm())
        .catch((err) => {
          dispatch(
            errorMessageChanged(
              err?.message || 'Error Updating Assignment Completion Threshold',
            ),
          )
          dispatch(fetchLinkedServices())
          setUpdatingAssignmentCompletionThreshold(false)
        })
    },
  })

  return (
    <Box>
      {editingAssignmentCompletionThreshold ? (
        <form onSubmit={form.handleSubmit}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TextField
              label="Threshold (months)"
              variant="outlined"
              size="small"
              name="assignmentCompletionThreshold"
              type="number"
              fullWidth
              value={form.values.assignmentCompletionThreshold}
              onChange={form.handleChange}
              error={
                form.touched.assignmentCompletionThreshold &&
                !!form.errors.assignmentCompletionThreshold
              }
              sx={{ flex: 1 }}
            />
            <Button
              color="error"
              size="small"
              onClick={() => {
                setEditingAssignmentCompletionThreshold(false)
                form.resetForm()
              }}
              disabled={updatingAssignmentCompletionThreshold}
            >
              <ClearIcon />
            </Button>
            <Button
              color="success"
              size="small"
              type="submit"
              disabled={updatingAssignmentCompletionThreshold}
            >
              {updatingAssignmentCompletionThreshold ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckIcon />
              )}
            </Button>
          </Box>
        </form>
      ) : (
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
            Completion Threshold
          </Typography>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {linkedService?.completionThreshold
              ? `${linkedService?.completionThreshold} months`
              : 'No threshold set'}
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
            onClick={() =>
              setEditingAssignmentCompletionThreshold(true)
            }
            sx={{ textTransform: 'none' }}
            disabled={!linkedService}
          >
            Edit Threshold
          </Button>
        </Box>
      )}
    </Box>
  )
}
